<template>
  <div class="ai-conversation-screen-wrapper">
    <loader :isShow="isLoading" :textInfo="''" />

    <transition name="sync-loader-fade">
      <div
        v-if="isSyncing"
        class="sync-loader-overlay"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="sync-loader-panel">
          <img
            alt=""
            class="sync-loader-gif"
            src="@/assets/rentalios.white.loader.gif"
          />
          <p class="sync-loader-title">Syncing conversations</p>
          <p class="sync-loader-subtitle">
            Fetching the latest messages from Uplisting. This may take a moment.
          </p>
        </div>
      </div>
    </transition>

    <div class="ai-conversation-screen">
      <section class="conversation-sidebar">
        <div class="sidebar-heading">
          <h2>Messages</h2>
          <button
            class="sync-button"
            type="button"
            :disabled="isSyncing"
            @click="syncConversations"
          >
            <span class="sync-icon">&#8635;</span>
            {{ isSyncing ? 'Syncing' : 'Sync' }}
          </button>
        </div>

        <input
          v-model="searchQuery"
          class="conversation-search"
          placeholder="Search messages"
          type="search"
        />
        <div v-if="syncError" class="sync-error">{{ syncError }}</div>

        <div class="conversation-tabs">
          <button
            :class="{ active: activeTab === 'all' }"
            type="button"
            @click="activeTab = 'all'"
          >
            All
          </button>
          <button
            :class="{ active: activeTab === 'unread' }"
            type="button"
            @click="activeTab = 'unread'"
          >
            Unread
          </button>
        </div>

        <div
          ref="conversationList"
          :class="['conversation-list', { 'is-paging': isLoadingList }]"
        >
          <button
            v-for="conversation in pagedConversations"
            :key="conversation.session_id"
            :class="[
              'conversation-row',
              { selected: conversation.session_id === selectedSessionId }
            ]"
            type="button"
            @click="selectConversation(conversation.session_id)"
            @mouseenter="prefetchSession(conversation.session_id)"
          >
            <span class="conversation-topline">
              <strong>{{ conversation.guest_name }}</strong>
              <span>{{ conversation.updated_at }}</span>
            </span>
            <span class="conversation-preview">
              <span>{{ conversation.last_message }}</span>
              <span v-if="conversation.unread" class="unread-count">
                {{ conversation.unread }}
              </span>
            </span>
            <span
              v-if="conversation.status"
              :class="['status-pill', statusClass(conversation.status)]"
            >
              {{ conversation.status }}
            </span>
          </button>
        </div>

        <div class="conversation-pagination">
          <div class="pagination-pages">
            <button
              class="pagination-nav"
              type="button"
              aria-label="First page"
              title="First page"
              :disabled="!canGoPrevPage"
              @click="selectPage(1)"
            >
              <svg
                aria-hidden="true"
                class="pagination-nav-icon"
                fill="none"
                viewBox="0 0 16 16"
              >
                <path
                  d="M7.25 3.5 2.75 8l4.5 4.5 M13.25 3.5 8.75 8l4.5 4.5"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </svg>
            </button>
            <button
              class="pagination-nav"
              type="button"
              aria-label="Previous page"
              :disabled="!canGoPrevPage"
              @click="goToPrevPage"
            >
              <svg
                aria-hidden="true"
                class="pagination-nav-icon"
                fill="none"
                viewBox="0 0 16 16"
              >
                <path
                  d="M10.25 3.5 5.75 8l4.5 4.5"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </svg>
            </button>
            <div class="pagination-numbers">
              <button
                v-for="page in pages"
                :key="page"
                :aria-current="page === currentPage ? 'page' : null"
                :class="[
                  'pagination-page',
                  { 'pagination-page--selected': page === currentPage }
                ]"
                type="button"
                @click="selectPage(page)"
              >
                {{ page }}
              </button>
              <span v-if="showPaginationEllipsis" class="pagination-ellipsis"
                >..</span
              >
            </div>
            <button
              class="pagination-nav"
              type="button"
              aria-label="Next page"
              :disabled="!canGoNextPage"
              @click="goToNextPage"
            >
              <svg
                aria-hidden="true"
                class="pagination-nav-icon"
                fill="none"
                viewBox="0 0 16 16"
              >
                <path
                  d="M5.75 3.5 10.25 8l-4.5 4.5"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </svg>
            </button>
            <button
              class="pagination-nav"
              type="button"
              aria-label="Last page"
              title="Last page"
              :disabled="!canGoNextPage"
              @click="selectPage(totalPages)"
            >
              <svg
                aria-hidden="true"
                class="pagination-nav-icon"
                fill="none"
                viewBox="0 0 16 16"
              >
                <path
                  d="M2.75 3.5 7.25 8l-4.5 4.5 M8.75 3.5 13.25 8l-4.5 4.5"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </svg>
            </button>
          </div>
          <span
            >Showing {{ pageSummary }} of
            {{ Math.max(totalConversationCount, 1) }}</span
          >
          <span>Total of {{ totalConversationCount }} messages</span>
        </div>
      </section>

      <section :class="['chat-workspace', { 'is-sending': isSending }]">
        <div
          v-if="isSending"
          class="chat-sending-progress"
          aria-hidden="true"
        ></div>
        <div class="chat-title-bar">
          <h1>{{ selectedGuestName }}</h1>
        </div>

        <div v-if="showNotGuestYetAlert" class="guest-alert">
          &#9432; This user is not a guest yet.
        </div>

        <div class="room-assignment-row">
          <label>
            <span>Property</span>
            <select
              v-model="editableProperty"
              :disabled="!isSandboxSelected"
              @change="selectProperty"
            >
              <option value="" disabled>Select a property</option>
              <option
                v-for="property in propertyOptions"
                :key="property"
                :value="property"
              >
                {{ property }}
              </option>
            </select>
          </label>
          <label class="room-assignment">
            <span>Room</span>
            <select
              v-model="editableRoom"
              :disabled="!isSandboxSelected"
              @change="selectRoom"
            >
              <option value="" disabled>Select a room</option>
              <option v-for="room in roomOptions" :key="room" :value="room">
                {{ room }}
              </option>
            </select>
          </label>
        </div>

        <div ref="messageStream" class="message-stream">
          <div v-if="!selectedSession" class="empty-message-state">
            Choose a conversation from the left to view messages.
          </div>
          <div
            v-else-if="isLoadingSession && selectedMessages.length === 0"
            class="messages-loading-state"
          >
            <div class="messages-loading-spinner" aria-hidden="true"></div>
            <span>Loading messages...</span>
          </div>
          <template v-else>
            <div
              v-for="(message, index) in selectedMessages"
              :key="message.message_id || message.id || index"
              :class="
                isSwitchMessage(message)
                  ? 'message-switch'
                  : [
                      'message-block',
                      messageBlockClass(message),
                      { 'message-block--compact': isCompactMessage(index) }
                    ]
              "
            >
              <template v-if="isSwitchMessage(message)">
                <span>{{ message.content }}</span>
              </template>
              <div v-else class="message-content">
                <div
                  class="message-bubble"
                  v-html="formatMessageContent(message.content)"
                ></div>
                <div v-if="showMessageActions(message)" class="message-footer">
                  <div class="message-actions">
                    <button
                      aria-label="Dislike response"
                      :disabled="
                        !isRealFeedbackMessageId(getFeedbackMessageId(message))
                      "
                      type="button"
                      @click="openFeedbackModal(message)"
                    >
                      &#128078;
                    </button>
                    <button
                      aria-label="Regenerate response"
                      class="regenerate-button"
                      type="button"
                    >
                      &#8635;
                    </button>
                  </div>
                  <div class="ai-generated-label">AI generated message</div>
                </div>
              </div>
            </div>
          </template>
          <div v-if="chatError" class="chat-error">{{ chatError }}</div>
        </div>

        <div :class="['chat-composer', { 'ai-draft-mode': isAiDraftMode }]">
          <template v-if="isAiDraftMode">
            <div class="ai-draft-editor">
              <span aria-hidden="true" class="ai-sparkle">&#10022;</span>
              <textarea
                v-model.trim="newConversationMessage"
                aria-label="AI-generated response draft"
                :disabled="!selectedSession || isSending"
              ></textarea>
            </div>
            <div class="ai-draft-toolbar">
              <button
                class="change-model-button"
                type="button"
                @click="openModelModal"
              >
                Change Model
              </button>
              <div class="message-actions">
                <button
                  aria-label="Reject AI response"
                  type="button"
                  @click="discardAiResponse"
                >
                  &#128078;
                </button>
                <button
                  aria-label="Regenerate AI response"
                  class="regenerate-button"
                  type="button"
                  @click="regenerateAiResponse"
                >
                  &#8635;
                </button>
              </div>
              <button
                :disabled="!newConversationMessage || !selectedSession"
                class="send-button approve-send-button"
                type="button"
                @click="approveAiResponse"
              >
                Approve and Send
              </button>
            </div>
          </template>
          <template v-else>
            <div
              v-if="canSwitchReplyRole"
              :class="[
                'sandbox-role-bar',
                { 'sandbox-role-bar--guest': isSandboxGuest }
              ]"
            >
              <div class="sandbox-role-copy">
                <span
                  :class="[
                    'sandbox-role-icon',
                    { 'sandbox-role-icon--guest': isSandboxGuest }
                  ]"
                  aria-hidden="true"
                >
                  <svg
                    v-if="isSandboxGuest"
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                    fill="currentColor"
                  >
                    <path
                      d="M12 12.2a4.1 4.1 0 1 0 0-8.2 4.1 4.1 0 0 0 0 8.2Zm0 1.6c-3.6 0-8 1.8-8 4.4V21h16v-2.8c0-2.6-4.4-4.4-8-4.4Z"
                    />
                  </svg>
                  <svg
                    v-else
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                  >
                    <path
                      d="M5 13.2v-2.4a7 7 0 0 1 14 0v2.4"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                    />
                    <rect
                      x="3.8"
                      y="11.4"
                      width="3.6"
                      height="6"
                      rx="1.5"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                    <rect
                      x="16.6"
                      y="11.4"
                      width="3.6"
                      height="6"
                      rx="1.5"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                  </svg>
                </span>
                <strong>{{ sandboxRoleTitle }}</strong>
                <span class="sandbox-role-hint">{{ sandboxRoleHint }}</span>
              </div>
              <button
                class="sandbox-role-switch"
                type="button"
                :disabled="isSending"
                @click="toggleSandboxRole"
              >
                <svg
                  viewBox="0 0 16 16"
                  width="14"
                  height="14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5.2 4.35 3.1 6.25 5.2 8.15"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3.1 6.25h4.1"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                  />
                  <circle cx="9.35" cy="6.25" r="0.72" fill="currentColor" />
                  <circle cx="11.35" cy="6.25" r="0.72" fill="currentColor" />
                  <circle cx="13.35" cy="6.25" r="0.72" fill="currentColor" />
                  <circle cx="2.65" cy="11.35" r="0.72" fill="currentColor" />
                  <circle cx="4.65" cy="11.35" r="0.72" fill="currentColor" />
                  <circle cx="6.65" cy="11.35" r="0.72" fill="currentColor" />
                  <path
                    d="M8.8 11.35h4.1"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                  />
                  <path
                    d="M10.8 9.45 12.9 11.35 10.8 13.25"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {{ sandboxRoleSwitchLabel }}
              </button>
            </div>
            <textarea
              v-model.trim="newConversationMessage"
              :disabled="!selectedSession || isSending"
              placeholder="Type your response"
            ></textarea>
            <div class="manual-composer-actions">
              <button
                class="change-model-button"
                type="button"
                @click="openModelModal"
              >
                Change Model
              </button>
              <div class="manual-composer-buttons">
                <button
                  :disabled="
                    !selectedSession || isSending || !canGenerateAiResponse
                  "
                  class="generate-ai-button"
                  type="button"
                  @click="generateAiResponse"
                >
                  <template v-if="isSending && sendingKind === 'draft'">
                    <span
                      class="send-button-spinner generate-button-spinner"
                      aria-hidden="true"
                    ></span>
                    Generating
                  </template>
                  <template v-else>
                    <span aria-hidden="true">&#10022;</span>
                    Generate AI Response
                  </template>
                </button>
                <button
                  :disabled="
                    !newConversationMessage || !selectedSession || isSending
                  "
                  class="send-button"
                  type="button"
                  @click="sendMessage"
                >
                  <span
                    v-if="isSending && sendingKind !== 'draft'"
                    class="send-button-spinner"
                    aria-hidden="true"
                  ></span>
                  {{
                    isSending && sendingKind !== 'draft' ? 'Sending' : 'Send'
                  }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <transition name="sending-fade">
          <div
            v-if="isSending"
            class="chat-sending-overlay chat-sending-indicator"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div class="sending-card">
              <div class="sending-orb" aria-hidden="true">
                <span class="sending-ring"></span>
                <span class="sending-sparkle">&#10022;</span>
              </div>
              <p class="sending-title">{{ sendingStatusTitle }}</p>
              <p class="sending-subtitle">{{ sendingStatusSubtitle }}</p>
              <span class="sending-dots" aria-hidden="true">
                <i></i>
                <i></i>
                <i></i>
              </span>
            </div>
          </div>
        </transition>
      </section>

      <aside class="guest-panel">
        <h2>{{ selectedGuestName }}</h2>
        <div class="guest-contact">
          <label>
            <span>Phone</span>
            <input
              v-model.trim="editableGuest.phone"
              :disabled="!isSandboxSelected"
              type="text"
            />
          </label>
        </div>

        <div class="guest-divider"></div>

        <div class="guest-field">
          <span>Name</span>
          <input
            v-model.trim="editableGuest.name"
            :disabled="!isSandboxSelected"
            type="text"
          />
        </div>
        <div class="guest-field">
          <span>Property</span>
          <input
            v-model.trim="editableGuest.property"
            :disabled="!isSandboxSelected"
            type="text"
          />
        </div>
        <div class="guest-field">
          <span>Guests</span>
          <input
            v-model.trim="editableGuest.guests"
            :disabled="!isSandboxSelected"
            type="text"
          />
        </div>
        <div class="guest-field">
          <span>Check-in</span>
          <input
            v-model.trim="editableGuest.checkIn"
            :disabled="!isSandboxSelected"
          />
        </div>
        <div class="guest-field">
          <span>Check-out</span>
          <input
            v-model.trim="editableGuest.checkOut"
            :disabled="!isSandboxSelected"
          />
        </div>
        <div class="guest-field first-message">
          <span>First Message</span>
          <strong>{{ selectedFirstMessage }}</strong>
        </div>
      </aside>
    </div>

    <FeedbackModal
      v-if="feedbackModal.isOpen"
      :feedback-modal="feedbackModal"
      :feedback-reasons="feedbackReasons"
      @close="closeFeedbackModal"
      @submit="submitNegativeFeedback"
      @toggle-reason="toggleFeedbackReason"
      @update-comment="updateFeedbackComment"
    />

    <div
      v-if="showModelModal"
      class="model-modal-overlay"
      role="dialog"
      aria-labelledby="choose-model-title"
      aria-modal="true"
      @keydown.esc.prevent
    >
      <div class="model-modal" @click.stop>
        <div class="model-modal-header">
          <h3 id="choose-model-title">Choose a Model</h3>
          <button
            aria-label="Close"
            class="model-modal-close"
            type="button"
            @click="closeModelModal"
          >
            &#10005;
          </button>
        </div>

        <div class="model-modal-body">
          <template v-if="isLoadingModels">
            <div
              class="model-loading"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <div class="model-loading-orb" aria-hidden="true">
                <span class="model-loading-ring"></span>
                <span class="model-loading-sparkle">&#10022;</span>
              </div>
              <p class="model-loading-title">Fetching available models</p>
              <p class="model-loading-subtitle">
                Checking OpenAI, Claude, and Gemini
              </p>
            </div>
          </template>

          <template v-else-if="modelsError">
            <div class="model-error-state" role="alert">
              <span class="model-error-icon" aria-hidden="true">&#9888;</span>
              <div class="model-error-body">
                <p class="model-error-message">{{ modelsError }}</p>
                <button
                  class="model-error-retry"
                  type="button"
                  @click="openModelModal"
                >
                  Try again
                </button>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="model-step">
              <div class="model-step-label">
                <span class="model-step-number" aria-hidden="true">1</span>
                <span>Choose a provider</span>
              </div>
              <multiselect
                v-model="pendingAiProvider"
                :allow-empty="false"
                :custom-label="providerDisplayName"
                :options="aiProviderOptions"
                :searchable="false"
                :show-labels="false"
                class="model-select"
                deselect-label=""
                placeholder="Select a provider"
                select-label=""
                selected-label=""
                @input="onPendingProviderChange"
              />
            </div>

            <div class="model-step">
              <div class="model-step-label">
                <span class="model-step-number" aria-hidden="true">2</span>
                <span>Choose a model</span>
              </div>
              <multiselect
                v-model="pendingAiModel"
                :allow-empty="false"
                :custom-label="modelDisplayName"
                :disabled="!pendingAiProvider"
                :options="pendingModelOptions"
                :searchable="false"
                :show-labels="false"
                class="model-select"
                deselect-label=""
                placeholder="Select a model"
                select-label=""
                selected-label=""
              />
            </div>
          </template>
        </div>

        <div class="model-modal-footer">
          <button
            class="model-modal-cancel"
            type="button"
            @click="closeModelModal"
          >
            Cancel
          </button>
          <button
            :disabled="isLoadingModels || !pendingAiProvider || !pendingAiModel"
            class="model-modal-confirm"
            type="button"
            @click="confirmModelChange"
          >
            Confirm Change
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAdminConversations,
  getAdminSession,
  syncUplistingConversations
} from '@/api/aiAdmin'
import calls from '@/api/calls'
import {
  getAvailableModels,
  sendChatFeedback,
  sendChatMessage
} from '@/api/chat'
import roomsApi from '@/api/rooms'
import Loader from '@/components/Loader.vue'
import { auth } from '@/Firebase'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import FeedbackModal from './components/FeedbackModal.vue'
import { buildChatContext } from './chatContext'

const PAGE_SIZE = 10
const PAGE_WINDOW = 5
const FETCH_SIZE = PAGE_SIZE * PAGE_WINDOW
const LIST_CACHE_TTL_MS = 60 * 1000
const SEARCH_DEBOUNCE_MS = 300

function parseAdminConversationsResult(result) {
  if (Array.isArray(result)) {
    return {
      conversations: result,
      total: result.length
    }
  }

  const conversations = (result && result.conversations) || []
  return {
    conversations,
    total: Number(result && result.total) || conversations.length
  }
}

function conversationWindowPage(page) {
  return Math.floor((Math.max(page, 1) - 1) / PAGE_WINDOW) + 1
}
const AI_MODEL_STORAGE_KEY = 'aiConversationSelectedModel'
const AI_PROVIDER_STORAGE_KEY = 'aiConversationSelectedProvider'
const SANDBOX_ROLE_STORAGE_KEY = 'aiSandboxReplyAs'
const SANDBOX_ROLE_AGENT = 'agent'
const SANDBOX_ROLE_GUEST = 'guest'
const OPENAI_PROVIDER_ID = 'openai'
const SANDBOX_ID = 'ai-sandbox'
const LOCAL_MESSAGE_ID_PREFIXES = ['assistant_', 'user_', 'sandbox_']
const SESSION_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes cache TTL

const feedbackReasons = [
  { id: 'incorrect', label: 'Incorrect or incomplete' },
  { id: 'not_what_asked', label: 'Not what I asked for' },
  { id: 'slow_or_buggy', label: 'Slow or buggy' },
  { id: 'style_tone', label: 'Style or tone' },
  { id: 'safety_legal', label: 'Safety or legal concern' },
  { id: 'other', label: 'Other' }
]

function isApiIdentifier(value) {
  return (
    Boolean(value) &&
    typeof value === 'string' &&
    value === value.toLowerCase() &&
    !/\s/.test(value)
  )
}

function resolveStoredAiSelection(savedProvider, savedModel) {
  if (isApiIdentifier(savedModel)) {
    return {
      provider: isApiIdentifier(savedProvider) ? savedProvider : '',
      model: savedModel
    }
  }

  return {
    provider: '',
    model: ''
  }
}

const sandboxConversation = {
  session_id: SANDBOX_ID,
  guest_name: 'AI Sandbox',
  room_property: 'Aurum',
  room: 'Aurum - B',
  phone: null,
  total_guest: null,
  status: '',
  updated_at: 'Now',
  unread: 0,
  last_message: 'Ask the assistant anything'
}
const sandboxWelcomeMessage = {
  role: 'assistant',
  content: 'Hi, how can I help?',
  message_id: 'sandbox_welcome'
}

function toApiMessage(message) {
  return {
    role: message.role === 'assistant' ? 'assistant' : 'user',
    content: message.content
  }
}

function ordinalSuffix(day) {
  const remainderTen = day % 10
  const remainderHundred = day % 100
  if (remainderTen === 1 && remainderHundred !== 11) {
    return 'st'
  }
  if (remainderTen === 2 && remainderHundred !== 12) {
    return 'nd'
  }
  if (remainderTen === 3 && remainderHundred !== 13) {
    return 'rd'
  }
  return 'th'
}

function formatSwitchTimestamp(date = new Date()) {
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' })
  const month = date.toLocaleDateString('en-US', { month: 'long' })
  const day = date.getDate()
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
  return `${weekday}, ${month} ${day}${ordinalSuffix(day)}, ${time}`
}

function isSandboxRole(value) {
  return value === SANDBOX_ROLE_GUEST || value === SANDBOX_ROLE_AGENT
}

function readStoredSandboxRoleMap() {
  const raw = localStorage.getItem(SANDBOX_ROLE_STORAGE_KEY)
  if (!raw || isSandboxRole(raw)) {
    return {}
  }

  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed
    }
  } catch {
    // Ignore corrupt storage and start from an empty map.
  }

  return {}
}

function normalizeMessageText(content) {
  return String(content)
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
}

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Applies lightweight markdown (bold/italic/inline code/links) to a single
// line of already HTML-escaped text.
function applyInlineMarkdown(text) {
  return text
    .replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_\n]+?)__/g, '<strong>$1</strong>')
    .replace(/`([^`\n]+?)`/g, '<code>$1</code>')
    .replace(
      /\[([^[\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+)/g,
      (match, linkText, linkHref, bareUrl) => {
        if (linkText && linkHref) {
          return `<a href="${linkHref}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
        }
        const trailingPunctuation = bareUrl.match(/[.,;:!?)]+$/)
        const trailing = trailingPunctuation ? trailingPunctuation[0] : ''
        const cleanUrl = trailing
          ? bareUrl.slice(0, bareUrl.length - trailing.length)
          : bareUrl
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${trailing}`
      }
    )
    .replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/(^|[^_])_([^_\n]+?)_(?!_)/g, '$1<em>$2</em>')
}

// Groups escaped, un-formatted lines into paragraph/list blocks, then applies
// inline markdown to each line/item so list markers are detected on raw text.
function buildMessageHtml(escapedText) {
  const blocks = []
  let listType = null
  let listItems = []
  let paragraphLines = []

  const flushList = () => {
    if (listType && listItems.length) {
      const items = listItems
        .map((item) => `<li>${applyInlineMarkdown(item)}</li>`)
        .join('')
      blocks.push(`<${listType}>${items}</${listType}>`)
    }
    listType = null
    listItems = []
  }

  const flushParagraph = () => {
    if (paragraphLines.length) {
      const html = paragraphLines.map(applyInlineMarkdown).join('<br>')
      blocks.push(`<p>${html}</p>`)
    }
    paragraphLines = []
  }

  escapedText.split('\n').forEach((line) => {
    const trimmed = line.trim()
    const bulletMatch = trimmed.match(/^[-*•]\s+(.*)$/)
    const orderedMatch = trimmed.match(/^\d+[.)]\s+(.*)$/)

    if (bulletMatch) {
      flushParagraph()
      if (listType !== 'ul') {
        flushList()
        listType = 'ul'
      }
      listItems.push(bulletMatch[1])
      return
    }

    if (orderedMatch) {
      flushParagraph()
      if (listType !== 'ol') {
        flushList()
        listType = 'ol'
      }
      listItems.push(orderedMatch[1])
      return
    }

    flushList()

    if (trimmed === '') {
      flushParagraph()
      return
    }

    paragraphLines.push(line)
  })

  flushList()
  flushParagraph()

  return blocks.join('')
}

export default {
  name: 'AIConversationsScreen',
  components: {
    FeedbackModal,
    Loader,
    Multiselect
  },
  data() {
    return {
      isLoading: true,
      isLoadingSession: false,
      conversations: [],
      selectedSessionId: null,
      selectedSession: null,
      searchQuery: '',
      activeTab: 'all',
      newConversationMessage: '',
      isAiDraftMode: false,
      currentPage: 1,
      conversationsTotal: 0,
      isLoadingList: false,
      conversationsRequestId: 0,
      conversationListCache: new Map(),
      searchDebounceTimer: null,
      sandboxSessionId: null,
      sandboxMessages: [sandboxWelcomeMessage],
      properties: [],
      roomsData: [],
      uplistingRooms: [],
      editableProperty: sandboxConversation.room_property,
      editableRoom: sandboxConversation.room,
      editableGuest: {
        name: '',
        email: '',
        phone: '',
        property: '',
        guests: '',
        checkIn: '',
        checkOut: ''
      },
      editableGuestSessionId: null,
      currentUser: null,
      unsubscribeAuth: null,
      isSending: false,
      sendingKind: '',
      isSyncing: false,
      chatError: '',
      syncError: '',
      showModelModal: false,
      isLoadingModels: false,
      modelsError: '',
      aiProviders: [],
      modelsByProvider: {},
      selectedAiProvider: '',
      selectedAiModel: '',
      pendingAiProvider: '',
      pendingAiModel: '',
      feedbackReasons,
      feedbackModal: {
        isOpen: false,
        messageId: null,
        reasons: [],
        comment: '',
        isSubmitting: false,
        isSubmitted: false,
        error: ''
      },
      sandboxReplyAs: SANDBOX_ROLE_GUEST,
      sandboxRoleBySession: readStoredSandboxRoleMap(),
      // Session caching for faster loading
      sessionCache: new Map(),
      pendingSessionRequest: null,
      prefetchAbortController: null
    }
  },
  computed: {
    aiProviderOptions() {
      return this.aiProviders.map((provider) => provider.id)
    },
    pendingModelOptions() {
      return this.modelsForProvider(this.pendingAiProvider).map(
        (model) => model.value
      )
    },
    filteredConversations() {
      return this.conversations.filter((conversation) => {
        if (conversation.session_id === SANDBOX_ID) {
          return true
        }

        const guestName = conversation.guest_name || ''
        const lastMessage = conversation.last_message || ''
        const search = this.searchQuery.toLowerCase()
        const matchesSearch = search
          ? guestName.toLowerCase().includes(search) ||
            lastMessage.toLowerCase().includes(search)
          : true
        const matchesTab =
          this.activeTab === 'unread' ? conversation.unread > 0 : true

        return matchesSearch && matchesTab
      })
    },
    apiConversations() {
      return this.filteredConversations.filter(
        (conversation) => conversation.session_id !== SANDBOX_ID
      )
    },
    sandboxConversationItem() {
      return this.filteredConversations.find(
        (conversation) => conversation.session_id === SANDBOX_ID
      )
    },
    totalConversationCount() {
      if (this.activeTab === 'unread') {
        return this.apiConversations.length
      }

      if (this.conversationsTotal > 0) {
        return this.conversationsTotal
      }

      return this.apiConversations.length
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalConversationCount / PAGE_SIZE))
    },
    pagedConversations() {
      const items = this.apiConversations
      const windowIndex = conversationWindowPage(this.currentPage) - 1
      const hasFullLocalList =
        this.conversationsTotal === 0 || items.length > FETCH_SIZE
      const pageStart = hasFullLocalList
        ? (this.currentPage - 1) * PAGE_SIZE
        : (this.currentPage - 1 - windowIndex * PAGE_WINDOW) * PAGE_SIZE
      const pageItems = items.slice(pageStart, pageStart + PAGE_SIZE)

      if (this.currentPage === 1 && this.sandboxConversationItem) {
        return [this.sandboxConversationItem, ...pageItems]
      }

      return pageItems
    },
    pages() {
      const start =
        (conversationWindowPage(this.currentPage) - 1) * PAGE_WINDOW + 1
      const end = Math.min(start + PAGE_WINDOW - 1, this.totalPages)
      if (end < start) {
        return [1]
      }

      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      )
    },
    showPaginationEllipsis() {
      return this.pages[this.pages.length - 1] < this.totalPages
    },
    canGoPrevPage() {
      return this.currentPage > 1
    },
    canGoNextPage() {
      return this.currentPage < this.totalPages
    },
    pageSummary() {
      if (!this.totalConversationCount) {
        return '0'
      }
      const pageStart = (this.currentPage - 1) * PAGE_SIZE + 1
      const pageEnd = Math.min(
        this.currentPage * PAGE_SIZE,
        this.totalConversationCount
      )
      return `${pageStart} - ${pageEnd}`
    },
    selectedSummary() {
      return (
        this.conversations.find(
          (conversation) => conversation.session_id === this.selectedSessionId
        ) || {}
      )
    },
    selectedMessages() {
      return this.selectedSession && this.selectedSession.messages
        ? this.selectedSession.messages
        : []
    },
    activeEditableGuest() {
      return this.editableGuestSessionId === this.selectedSessionId
        ? this.editableGuest
        : {}
    },
    selectedGuestName() {
      return (
        this.activeEditableGuest.name ||
        this.selectedSummary.guest_name ||
        (this.selectedSession && this.selectedSession.guest_name) ||
        'Guest details'
      )
    },
    selectedPhone() {
      return (
        this.activeEditableGuest.phone ||
        (this.selectedSession && this.selectedSession.phone) ||
        this.selectedSummary.phone ||
        'Not provided'
      )
    },
    selectedProperty() {
      return (
        this.propertyFromRoom(this.selectedRoom) ||
        this.editableProperty ||
        (this.selectedSession && this.selectedSession.room_property) ||
        this.selectedSummary.room_property ||
        'Aurum'
      )
    },
    propertyOptions() {
      // Extract unique property names from uplistingRooms.
      const uplistingNames = [
        ...new Set(
          this.uplistingRooms
            .map((nickname) => {
              const parts = nickname.split(' - ')
              return parts.length > 1 ? parts[0].trim() : null
            })
            .filter(Boolean)
        )
      ].sort()

      // Also include API properties for any that might not be in uplisting.
      const apiNames = (this.properties || [])
        .filter((p) => p && p.name)
        .map((p) => p.name)

      const merged = [...new Set([...uplistingNames, ...apiNames])]

      if (this.selectedProperty && !merged.includes(this.selectedProperty)) {
        return [this.selectedProperty, ...merged]
      }

      return merged
    },
    roomOptions() {
      if (!this.editableProperty) {
        return []
      }

      const prefix = this.editableProperty + ' - '
      return this.uplistingRooms
        .filter((nickname) => nickname.startsWith(prefix))
        .sort()
    },
    selectedRoom() {
      if (this.isInitialSandbox) {
        return this.editableRoom || sandboxConversation.room
      }

      return (
        (this.selectedSession && this.selectedSession.room) ||
        this.selectedSummary.room ||
        'Not Assigned'
      )
    },
    selectedGuests() {
      if (this.activeEditableGuest.guests) {
        return this.activeEditableGuest.guests
      }

      const totalGuest =
        (this.selectedSession && this.selectedSession.total_guest) ??
        this.selectedSummary.total_guest
      if (totalGuest != null && totalGuest !== '') {
        return this.formatGuestCount(totalGuest)
      }

      return (
        (this.selectedSession && this.selectedSession.guests) ||
        this.selectedSummary.guests ||
        'Not provided'
      )
    },
    selectedFirstMessage() {
      return (
        (this.selectedSession && this.selectedSession.first_message_at) ||
        this.selectedSummary.first_message_at ||
        'June 30th at 2:34 PM'
      )
    },
    isInitialSandbox() {
      return this.selectedSessionId === SANDBOX_ID
    },
    isSavedSandboxChat() {
      if (!this.selectedSessionId || this.selectedSessionId === SANDBOX_ID) {
        return false
      }

      return (
        this.selectedSessionId === this.sandboxSessionId ||
        isSandboxRole(this.sandboxRoleBySession[this.selectedSessionId])
      )
    },
    isSandboxSelected() {
      return this.isInitialSandbox || this.isSavedSandboxChat
    },
    isSandboxGuest() {
      return this.sandboxReplyAs === SANDBOX_ROLE_GUEST
    },
    sandboxRoleTitle() {
      return this.isSandboxGuest ? 'Replying as GUEST' : 'Replying as an AGENT'
    },
    sandboxRoleHint() {
      return this.isSandboxGuest
        ? 'Anything you type below will be sent as the guest.'
        : 'Anything you type below will be sent as the agent.'
    },
    sandboxRoleSwitchLabel() {
      return this.isSandboxGuest ? 'Switch to Agent' : 'Switch to Guest'
    },
    canGenerateAiResponse() {
      if (!this.canSwitchReplyRole) {
        return true
      }
      return this.selectedMessages.some(
        (message) =>
          message && !this.isSwitchMessage(message) && message.role === 'user'
      )
    },
    selectedSource() {
      return (
        (this.selectedSession && this.selectedSession.source) ||
        this.selectedSummary.source ||
        null
      )
    },
    showNotGuestYetAlert() {
      if (!this.selectedSessionId || this.isInitialSandbox) {
        return false
      }

      // Uplisting conversations are already guests — hide the alert.
      // Saved sandbox/widget chats still need it: they are not guests yet.
      return this.selectedSource !== 'uplisting'
    },
    canSwitchReplyRole() {
      return this.isSandboxSelected || this.showNotGuestYetAlert
    },
    currentUserName() {
      return (
        (this.currentUser && this.currentUser.displayName) ||
        this.selectedGuestName
      )
    },
    sendingStatusTitle() {
      return this.sendingKind === 'agent'
        ? 'Sending your reply'
        : 'Generating a response'
    },
    sendingStatusSubtitle() {
      return this.sendingKind === 'agent'
        ? 'Saving this message to the conversation.'
        : 'The assistant is writing a reply. This may take a moment.'
    }
  },
  watch: {
    activeTab() {
      this.currentPage = 1
      this.loadConversations({ useFullLoader: false })
    },
    searchQuery() {
      this.currentPage = 1
      this.scheduleConversationsSearch()
    }
  },
  methods: {
    conversationListCacheKey(windowPage) {
      return `${windowPage}|${this.activeTab}|${(this.searchQuery || '')
        .trim()
        .toLowerCase()}`
    },
    applyConversationPage({ conversations, total }, previousSessionId) {
      this.conversations = [
        sandboxConversation,
        ...conversations.filter(
          (conversation) => conversation.session_id !== SANDBOX_ID
        )
      ]
      this.conversationsTotal = total

      if (!previousSessionId) {
        this.selectConversation(SANDBOX_ID)
      }
    },
    scheduleConversationsSearch() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounceTimer = null
        this.loadConversations({ useFullLoader: false })
      }, SEARCH_DEBOUNCE_MS)
    },
    prefetchConversationWindow(windowPage) {
      const cacheKey = this.conversationListCacheKey(windowPage)
      if (this.conversationListCache.has(cacheKey)) {
        return
      }

      getAdminConversations({
        page: windowPage,
        limit: FETCH_SIZE,
        q: (this.searchQuery || '').trim()
      })
        .then((result) => {
          const parsed = parseAdminConversationsResult(result)
          this.conversationListCache.set(cacheKey, {
            ...parsed,
            fetchedAt: Date.now()
          })
        })
        .catch(() => {})
    },
    async loadConversations({ useFullLoader = null } = {}) {
      const previousSessionId = this.selectedSessionId
      const windowPage = conversationWindowPage(this.currentPage)
      const cacheKey = this.conversationListCacheKey(windowPage)
      const cached = this.conversationListCache.get(cacheKey)
      if (cached && Date.now() - cached.fetchedAt < LIST_CACHE_TTL_MS) {
        this.applyConversationPage(cached, previousSessionId)
        this.isLoading = false
        this.isLoadingList = false
        return
      }

      const requestId = this.conversationsRequestId + 1
      this.conversationsRequestId = requestId
      const showFullLoader =
        useFullLoader != null ? useFullLoader : this.conversations.length === 0
      if (showFullLoader) {
        this.isLoading = true
      } else {
        this.isLoadingList = true
      }

      try {
        const result = await getAdminConversations({
          page: windowPage,
          limit: FETCH_SIZE,
          q: (this.searchQuery || '').trim()
        })
        if (requestId !== this.conversationsRequestId) {
          return
        }

        const parsed = parseAdminConversationsResult(result)
        this.conversationListCache.set(cacheKey, {
          ...parsed,
          fetchedAt: Date.now()
        })
        this.applyConversationPage(parsed, previousSessionId)

        const loadedThrough = windowPage * FETCH_SIZE
        if (parsed.total > loadedThrough) {
          this.prefetchConversationWindow(windowPage + 1)
        }
      } catch (error) {
        if (requestId === this.conversationsRequestId) {
          this.syncError = error.message
        }
      } finally {
        if (requestId === this.conversationsRequestId) {
          this.isLoading = false
          this.isLoadingList = false
        }
      }
    },
    async syncConversations() {
      this.isSyncing = true
      this.syncError = ''

      try {
        await syncUplistingConversations()
        // Clear cache since conversations may have changed
        this.invalidateSessionCache()
        this.conversationListCache.clear()
        this.currentPage = 1
        await this.loadConversations()
      } catch (error) {
        this.syncError = error.message
      } finally {
        this.isSyncing = false
      }
    },
    async loadProperties() {
      const { data } = await calls.GET('properties')
      this.properties = Array.isArray(data) ? data : []
    },
    async loadRooms() {
      try {
        const response = await roomsApi.getRooms({
          page: 1,
          perPage: 100,
          occupancy: 'green,yellow,red'
        })
        const rooms = response && response.data
        if (Array.isArray(rooms) && rooms.length) {
          this.roomsData = rooms
          // Build the nickname list: "<propertyName> - <internalName>"
          const nicknames = rooms
            .filter((r) => r.propertyName && r.internalName)
            .map((r) => `${r.propertyName} - ${r.internalName}`)
            .sort()
          this.uplistingRooms = nicknames
        }
      } catch {
        // Silently fall back to empty list
      }
    },
    async selectConversation(sessionId) {
      // Cancel any pending session request
      if (this.pendingSessionRequest) {
        this.pendingSessionRequest.abort()
        this.pendingSessionRequest = null
      }

      this.selectedSessionId = sessionId
      this.chatError = ''
      this.resetComposer()
      this.applySandboxRole(sessionId)

      if (sessionId === SANDBOX_ID) {
        this.isLoadingSession = false
        this.editableProperty = sandboxConversation.room_property
        this.editableRoom = this.editableRoom || sandboxConversation.room
        this.setEditableGuest(sandboxConversation)
        this.selectedSession = {
          ...sandboxConversation,
          session_id: this.sandboxSessionId || SANDBOX_ID,
          messages: this.sandboxMessages
        }
        this.syncSelectedAssignment()
        this.scrollMessagesToBottom()
        return
      }

      // Get conversation summary from sidebar for optimistic UI
      const summary =
        this.conversations.find(
          (conversation) => conversation.session_id === sessionId
        ) || {}

      // Check cache first for instant loading
      const cached = this.getSessionFromCache(sessionId)
      if (cached) {
        this.applySessionData(cached, summary)
        this.scrollMessagesToBottom()
        return
      }

      // Show optimistic UI immediately with sidebar data (no loading spinner for partial data)
      this.showOptimisticSession(summary)

      // Then fetch full session data
      this.isLoadingSession = true
      const abortController = new AbortController()
      this.pendingSessionRequest = abortController

      try {
        const session = await getAdminSession(sessionId, abortController.signal)

        // User may have clicked another conversation while loading
        if (this.selectedSessionId !== sessionId) {
          return
        }

        // Cache the session
        this.cacheSession(sessionId, session)

        // Apply full session data
        this.applySessionData(session, summary)
        this.scrollMessagesToBottom()
      } catch (error) {
        // Ignore abort errors
        if (error.name === 'AbortError') {
          return
        }
        this.chatError = error.message || 'Failed to load conversation.'
      } finally {
        if (this.selectedSessionId === sessionId) {
          this.isLoadingSession = false
        }
        this.pendingSessionRequest = null
      }
    },
    showOptimisticSession(summary) {
      const room = summary.room || 'Not Assigned'
      const propertyName =
        this.propertyFromRoom(room) || summary.room_property || 'Aurum'
      this.editableProperty = propertyName
      this.editableRoom = this.findRoomNickname(room, propertyName)

      // Show partial data immediately - messages will load async
      this.selectedSession = {
        ...summary,
        room_property: propertyName,
        room: room,
        messages: [] // Empty initially, will populate when loaded
      }
      this.setEditableGuest(this.selectedSession)
    },
    applySessionData(session, summary) {
      const room = summary.room || session.room || 'Not Assigned'
      const propertyName =
        this.propertyFromRoom(room) ||
        session.room_property ||
        summary.room_property ||
        'Aurum'
      this.editableProperty = propertyName
      this.editableRoom = this.findRoomNickname(room, propertyName)

      this.selectedSession = {
        ...summary,
        ...session,
        email: session.email || summary.email || null,
        phone: session.phone || summary.phone || null,
        total_guest:
          session.total_guest != null
            ? session.total_guest
            : summary.total_guest,
        room_property: propertyName,
        room: session.room || summary.room || 'Not Assigned',
        messages: session.messages || []
      }
      this.setEditableGuest(this.selectedSession)
    },
    getSessionFromCache(sessionId) {
      const entry = this.sessionCache.get(sessionId)
      if (!entry) {
        return null
      }
      // Check if cache entry is still valid
      if (Date.now() - entry.timestamp > SESSION_CACHE_TTL_MS) {
        this.sessionCache.delete(sessionId)
        return null
      }
      return entry.data
    },
    cacheSession(sessionId, session) {
      this.sessionCache.set(sessionId, {
        data: session,
        timestamp: Date.now()
      })
    },
    invalidateSessionCache(sessionId) {
      if (sessionId) {
        this.sessionCache.delete(sessionId)
      } else {
        this.sessionCache.clear()
      }
    },
    prefetchSession(sessionId) {
      // Don't prefetch sandbox or already cached sessions
      if (sessionId === SANDBOX_ID || this.getSessionFromCache(sessionId)) {
        return
      }

      // Cancel any existing prefetch
      if (this.prefetchAbortController) {
        this.prefetchAbortController.abort()
      }

      const abortController = new AbortController()
      this.prefetchAbortController = abortController

      // Fire and forget - prefetch in background
      getAdminSession(sessionId, abortController.signal)
        .then((session) => {
          if (!abortController.signal.aborted) {
            this.cacheSession(sessionId, session)
          }
        })
        .catch(() => {
          // Silently ignore prefetch errors
        })
        .finally(() => {
          if (this.prefetchAbortController === abortController) {
            this.prefetchAbortController = null
          }
        })
    },
    selectProperty() {
      if (this.roomOptions.length) {
        this.editableRoom = this.roomOptions[0]
      } else {
        this.editableRoom = ''
      }
      this.syncSelectedAssignment()
    },
    selectRoom() {
      this.syncSelectedAssignment()
    },
    syncSelectedAssignment() {
      if (!this.selectedSession) {
        return
      }

      this.selectedSession.room_property = this.editableProperty
      this.selectedSession.room = this.editableRoom
    },
    async sendMessage() {
      if (
        !this.selectedSession ||
        !this.newConversationMessage ||
        this.isSending
      ) {
        return
      }

      const content = this.newConversationMessage

      if (this.canSwitchReplyRole) {
        this.resetComposer()
        if (this.isSandboxGuest) {
          this.appendMessage({
            role: 'user',
            content,
            message_id: `user_${Date.now()}`
          })
          this.scrollMessagesToBottom()
          await this.requestAiResponse({
            asDraft: false,
            senderRole: SANDBOX_ROLE_GUEST
          })
          return
        }

        await this.sendSandboxAgentMessage(content)
        return
      }

      this.appendOutgoingMessage(content, 'agent')
      this.resetComposer()
    },
    async generateAiResponse() {
      if (!this.selectedSession || this.isSending) {
        return
      }

      await this.requestAiResponse({ asDraft: true })
    },
    async regenerateAiResponse() {
      await this.generateAiResponse()
    },
    async requestAiResponse({ asDraft, senderRole }) {
      this.sendingKind = asDraft ? 'draft' : 'ai'
      this.isSending = true
      this.chatError = ''
      this.scrollMessagesToBottom()

      try {
        await this.ensureModelsCatalog()
        this.applySelectionFromCatalog()
      } catch {
        // Keep any existing selection if the catalog cannot be loaded.
      }

      try {
        this.syncSelectedAssignment()
        const response = await sendChatMessage({
          sessionId: this.chatSessionId(),
          messages: this.apiMessages(),
          context: this.buildChatRequestContext(),
          senderRole: senderRole || undefined
        })
        const reply = response.reply || response.content || response.message

        if (!reply) {
          throw new Error('The AI agent returned an empty response.')
        }

        if (this.canSwitchReplyRole && response.session_id) {
          if (this.isInitialSandbox) {
            this.sandboxSessionId = response.session_id
          }
          this.rememberSandboxRole(response.session_id)
        }
        this.selectedSession.session_id =
          response.session_id || this.selectedSession.session_id

        if (asDraft) {
          this.isAiDraftMode = true
          this.newConversationMessage = reply
        } else {
          this.appendMessage({
            role: 'assistant',
            content: reply,
            source: 'widget',
            message_id: response.message_id || `assistant_${Date.now()}`
          })
          this.updateSandboxPreview(reply)
          this.scrollMessagesToBottom()
        }
      } catch (error) {
        this.chatError = error.message
      } finally {
        this.isSending = false
        this.sendingKind = ''
        if (!asDraft) {
          this.scrollMessagesToBottom()
        }
      }
    },
    applySandboxRole(sessionId) {
      if (sessionId === SANDBOX_ID) {
        if (this.sandboxSessionId) {
          const saved = this.sandboxRoleBySession[this.sandboxSessionId]
          if (isSandboxRole(saved)) {
            this.sandboxReplyAs = saved
          }
          return
        }

        this.sandboxReplyAs = SANDBOX_ROLE_GUEST
        return
      }

      const saved = this.sandboxRoleBySession[sessionId]
      this.sandboxReplyAs = isSandboxRole(saved) ? saved : SANDBOX_ROLE_GUEST
    },
    rememberSandboxRole(sessionId) {
      if (!sessionId || sessionId === SANDBOX_ID) {
        return
      }

      this.sandboxRoleBySession = {
        ...this.sandboxRoleBySession,
        [sessionId]: this.sandboxReplyAs
      }
      localStorage.setItem(
        SANDBOX_ROLE_STORAGE_KEY,
        JSON.stringify(this.sandboxRoleBySession)
      )
    },
    persistCurrentSandboxRole() {
      this.rememberSandboxRole(
        this.sandboxSessionId ||
          (this.selectedSessionId !== SANDBOX_ID
            ? this.selectedSessionId
            : null)
      )
    },
    toggleSandboxRole() {
      if (!this.canSwitchReplyRole || this.isSending) {
        return
      }

      const fromLabel = this.isSandboxGuest ? 'Guest' : 'Agent'
      const nextRole = this.isSandboxGuest
        ? SANDBOX_ROLE_AGENT
        : SANDBOX_ROLE_GUEST
      const toLabel = nextRole === SANDBOX_ROLE_GUEST ? 'Guest' : 'Agent'

      this.sandboxReplyAs = nextRole
      this.persistCurrentSandboxRole()
      this.appendMessage({
        role: 'system',
        type: 'switch',
        content: `Conversation switched from ${fromLabel} to ${toLabel} | ${formatSwitchTimestamp()}`,
        message_id: `switch_${Date.now()}`
      })
      this.scrollMessagesToBottom()
    },
    async sendSandboxAgentMessage(content) {
      const message = {
        role: 'assistant',
        content,
        source: 'agent',
        message_id: `assistant_${Date.now()}`
      }
      this.appendMessage(message)
      this.updateSandboxPreview(content)

      this.sendingKind = 'agent'
      this.isSending = true
      this.chatError = ''
      this.scrollMessagesToBottom()

      try {
        const response = await sendChatMessage({
          sessionId: this.chatSessionId(),
          messages: this.apiMessages(),
          context: this.buildChatRequestContext(),
          senderRole: SANDBOX_ROLE_AGENT
        })

        if (this.canSwitchReplyRole && response.session_id) {
          if (this.isInitialSandbox) {
            this.sandboxSessionId = response.session_id
          }
          this.rememberSandboxRole(response.session_id)
        }
        this.selectedSession.session_id =
          response.session_id || this.selectedSession.session_id
        if (response.message_id) {
          message.message_id = response.message_id
        }
      } catch (error) {
        this.chatError = error.message
      } finally {
        this.isSending = false
        this.sendingKind = ''
        this.scrollMessagesToBottom()
      }
    },
    loadSavedAiModel() {
      const selection = resolveStoredAiSelection(
        localStorage.getItem(AI_PROVIDER_STORAGE_KEY),
        localStorage.getItem(AI_MODEL_STORAGE_KEY)
      )

      this.selectedAiProvider = selection.provider
      this.selectedAiModel = selection.model
      this.pendingAiProvider = selection.provider
      this.pendingAiModel = selection.model
    },
    modelsForProvider(providerId) {
      const fromMap = this.modelsByProvider[providerId]
      if (Array.isArray(fromMap) && fromMap.length) {
        return fromMap
      }

      const provider = this.aiProviders.find((item) => item.id === providerId)
      if (!provider || !Array.isArray(provider.models)) {
        return []
      }

      return provider.models.map((model) => ({
        value: model.id,
        label: model.name,
        description: model.description
      }))
    },
    defaultModelForProvider(providerId) {
      const models = this.modelsForProvider(providerId)
      return (models[0] && models[0].value) || null
    },
    defaultsFromOpenAiCatalog() {
      const openai = this.aiProviders.find(
        (item) => item.id === OPENAI_PROVIDER_ID
      )
      const openaiModels = Array.isArray(openai && openai.models)
        ? openai.models
        : []
      const firstOpenAiModel = openaiModels[0]
      const mappedFirst =
        this.modelsByProvider[OPENAI_PROVIDER_ID] &&
        this.modelsByProvider[OPENAI_PROVIDER_ID][0]

      return {
        provider: (openai && openai.id) || OPENAI_PROVIDER_ID,
        model:
          (firstOpenAiModel && firstOpenAiModel.id) ||
          (mappedFirst && mappedFirst.value) ||
          null
      }
    },
    isSelectionInCatalog(providerId, modelId) {
      return this.modelsForProvider(providerId).some(
        (model) => model.value === modelId
      )
    },
    applySelectionFromCatalog() {
      if (
        this.isSelectionInCatalog(this.selectedAiProvider, this.selectedAiModel)
      ) {
        this.pendingAiProvider = this.selectedAiProvider
        this.pendingAiModel = this.selectedAiModel
        return
      }

      if (this.selectedAiModel) {
        const owner = this.aiProviders.find((provider) =>
          this.isSelectionInCatalog(provider.id, this.selectedAiModel)
        )
        if (owner) {
          this.selectedAiProvider = owner.id
          this.pendingAiProvider = owner.id
          this.pendingAiModel = this.selectedAiModel
          return
        }
      }

      const defaults = this.defaultsFromOpenAiCatalog()
      this.selectedAiProvider = defaults.provider
      this.selectedAiModel = defaults.model
      this.pendingAiProvider = defaults.provider
      this.pendingAiModel = defaults.model
    },
    providerDisplayName(providerId) {
      const provider = this.aiProviders.find((item) => item.id === providerId)
      return (provider && provider.name) || providerId
    },
    modelDisplayName(modelId) {
      const model = this.modelsForProvider(this.pendingAiProvider).find(
        (item) => item.value === modelId
      )
      return (model && model.label) || modelId
    },
    applyModelsResponse(data) {
      this.aiProviders = Array.isArray(data && data.providers)
        ? data.providers
        : []
      const catalog = data && data.models_by_provider
      this.modelsByProvider =
        catalog && typeof catalog === 'object' ? catalog : {}
    },
    async ensureModelsCatalog() {
      if (this.aiProviders.length) {
        return
      }

      const data = await getAvailableModels()
      this.applyModelsResponse(data)
    },
    async openModelModal() {
      this.pendingAiProvider = this.selectedAiProvider
      this.pendingAiModel = this.selectedAiModel
      this.modelsError = ''
      this.isLoadingModels = true
      this.showModelModal = true

      try {
        const data = await getAvailableModels()
        this.applyModelsResponse(data)
        this.applySelectionFromCatalog()
      } catch (error) {
        this.modelsError = error.message || 'Failed to load models.'
      } finally {
        this.isLoadingModels = false
      }
    },
    closeModelModal() {
      this.pendingAiProvider = this.selectedAiProvider
      this.pendingAiModel = this.selectedAiModel
      this.showModelModal = false
      this.modelsError = ''
      this.isLoadingModels = false
    },
    onPendingProviderChange(provider) {
      const models = this.modelsForProvider(provider)
      this.pendingAiModel = models.some(
        (model) => model.value === this.selectedAiModel
      )
        ? this.selectedAiModel
        : this.defaultModelForProvider(provider)
    },
    confirmModelChange() {
      if (
        this.isLoadingModels ||
        !this.pendingAiProvider ||
        !this.pendingAiModel
      ) {
        return
      }

      this.selectedAiProvider = this.pendingAiProvider
      this.selectedAiModel = this.pendingAiModel
      localStorage.setItem(AI_PROVIDER_STORAGE_KEY, this.selectedAiProvider)
      localStorage.setItem(AI_MODEL_STORAGE_KEY, this.selectedAiModel)
      this.showModelModal = false
    },
    discardAiResponse() {
      this.resetComposer()
    },
    approveAiResponse() {
      if (!this.selectedSession || !this.newConversationMessage) {
        return
      }

      const content = this.newConversationMessage
      this.appendOutgoingMessage(content, 'widget')
      this.resetComposer()
    },
    appendOutgoingMessage(content, source) {
      this.appendMessage({
        role: 'assistant',
        content,
        source,
        message_id: `assistant_${Date.now()}`
      })
      this.updateSandboxPreview(content)
      this.scrollMessagesToBottom()
    },
    resetComposer() {
      this.newConversationMessage = ''
      this.isAiDraftMode = false
    },
    getFeedbackMessageId(message) {
      return message && (message.message_id || message.id || null)
    },
    isRealFeedbackMessageId(messageId) {
      return (
        Boolean(messageId) &&
        !LOCAL_MESSAGE_ID_PREFIXES.some((prefix) =>
          String(messageId).startsWith(prefix)
        )
      )
    },
    openFeedbackModal(message) {
      const messageId = this.getFeedbackMessageId(message)
      if (!this.isRealFeedbackMessageId(messageId)) {
        return
      }

      this.feedbackModal = {
        isOpen: true,
        messageId,
        reasons: [],
        comment: '',
        isSubmitting: false,
        isSubmitted: false,
        error: ''
      }
    },
    closeFeedbackModal() {
      if (this.feedbackModal.isSubmitting) {
        return
      }

      this.feedbackModal = {
        isOpen: false,
        messageId: null,
        reasons: [],
        comment: '',
        isSubmitting: false,
        isSubmitted: false,
        error: ''
      }
    },
    toggleFeedbackReason(reasonId) {
      const reasons = this.feedbackModal.reasons.includes(reasonId)
        ? this.feedbackModal.reasons.filter((item) => item !== reasonId)
        : [...this.feedbackModal.reasons, reasonId]

      this.feedbackModal = {
        ...this.feedbackModal,
        reasons
      }
    },
    updateFeedbackComment(comment) {
      this.feedbackModal = {
        ...this.feedbackModal,
        comment
      }
    },
    async submitNegativeFeedback() {
      if (!this.isRealFeedbackMessageId(this.feedbackModal.messageId)) {
        return
      }

      this.feedbackModal.isSubmitting = true
      this.feedbackModal.error = ''

      try {
        await this.sendFeedback(this.feedbackModal.messageId, -1, {
          comment: this.feedbackModal.comment || null,
          reasons: this.feedbackModal.reasons
        })
        this.feedbackModal = {
          ...this.feedbackModal,
          isSubmitting: false,
          isSubmitted: true,
          error: ''
        }
      } catch (error) {
        this.feedbackModal.error = error.message
        this.feedbackModal.isSubmitting = false
      }
    },
    async sendFeedback(messageId, rating, options = {}) {
      if (!this.isRealFeedbackMessageId(messageId)) {
        return
      }

      try {
        await sendChatFeedback({
          messageId,
          rating,
          comment: options.comment || null,
          reasons: options.reasons || []
        })
      } catch (error) {
        this.chatError = error.message
        throw error
      }
    },
    updateSandboxPreview(lastMessage) {
      this.conversations = this.conversations.map((conversation) => {
        if (conversation.session_id !== this.selectedSessionId) {
          return conversation
        }

        return {
          ...conversation,
          updated_at: 'Now',
          last_message: lastMessage
        }
      })
    },
    appendMessage(message) {
      if (this.isInitialSandbox) {
        this.sandboxMessages = [...this.sandboxMessages, message]
        this.selectedSession.messages = this.sandboxMessages
        return
      }

      this.selectedSession.messages = [...this.selectedMessages, message]
      // Invalidate cache since messages changed
      this.invalidateSessionCache(this.selectedSessionId)
    },
    chatSessionId() {
      if (this.isInitialSandbox) {
        return this.sandboxSessionId
      }

      return this.selectedSession.session_id || this.selectedSessionId
    },
    apiMessages() {
      return this.selectedMessages
        .filter(
          (message) =>
            message && !this.isSwitchMessage(message) && message.content
        )
        .map(toApiMessage)
    },
    buildChatRequestContext() {
      return buildChatContext({
        propertyName: this.editableProperty || this.selectedProperty,
        room: this.editableRoom || this.selectedRoom,
        guestName: this.isInitialSandbox
          ? this.currentUserName
          : this.selectedGuestName,
        source: this.selectedSummary.source || 'widget',
        provider: this.selectedAiProvider,
        model: this.selectedAiModel
      })
    },
    setEditableGuest(source) {
      this.editableGuestSessionId = this.selectedSessionId
      this.editableGuest = {
        name: source.guest_name || '',
        email: source.email || 'Not provided',
        phone: source.phone || 'Not provided',
        property: source.room || 'Not Assigned',
        guests: this.formatGuestCount(source.total_guest ?? source.guests),
        checkIn: source.check_in_label || source.check_in || 'Not provided',
        checkOut: source.check_out_label || source.check_out || 'Not provided'
      }
    },
    formatGuestCount(value) {
      if (value == null || value === '') {
        return 'Not provided'
      }

      return String(value)
    },
    selectPage(page) {
      const nextPage = Math.min(Math.max(page, 1), this.totalPages)
      const currentWindow = conversationWindowPage(this.currentPage)
      const nextWindow = conversationWindowPage(nextPage)
      this.currentPage = nextPage
      this.scrollConversationListToTop()
      if (nextWindow !== currentWindow) {
        this.loadConversations({ useFullLoader: false })
      }
    },
    goToPrevPage() {
      if (!this.canGoPrevPage) {
        return
      }

      this.selectPage(this.currentPage - 1)
    },
    goToNextPage() {
      if (!this.canGoNextPage) {
        return
      }

      this.selectPage(this.currentPage + 1)
    },
    scrollConversationListToTop() {
      this.$nextTick(() => {
        if (this.$refs.conversationList) {
          this.$refs.conversationList.scrollTop = 0
        }
      })
    },
    scrollMessagesToBottom() {
      const alignToLatest = () => {
        const stream = this.$refs.messageStream
        if (!stream) {
          return
        }

        stream.scrollTop = stream.scrollHeight

        const lastMessage = stream.lastElementChild
        if (lastMessage && typeof lastMessage.scrollIntoView === 'function') {
          lastMessage.scrollIntoView({ block: 'end', behavior: 'auto' })
        }
      }

      this.$nextTick(() => {
        alignToLatest()
        requestAnimationFrame(() => {
          alignToLatest()
        })
      })
    },
    findRoomNickname(room, propertyName) {
      if (!room || room === 'Not Assigned') {
        return room
      }

      const normalized = String(room).trim()
      const rooms = this.uplistingRooms

      // Already in "Property - Letter" format and exists in uplistingRooms.
      if (normalized.includes(' - ')) {
        if (rooms.includes(normalized)) return normalized
      }

      // Try to build "Property - Letter" from "Property Letter" (e.g. "Aurum B" -> "Aurum - B").
      if (propertyName) {
        const roomPart = normalized.replace(propertyName, '').trim()
        if (roomPart) {
          const candidate = `${propertyName} - ${roomPart}`
          if (rooms.includes(candidate)) return candidate
        }
      }

      // Fallback: search all nicknames for a match.
      const match = rooms.find((nickname) => {
        return (
          nickname.toLowerCase() === normalized.toLowerCase() ||
          normalized
            .toLowerCase()
            .includes(nickname.split(' - ')[1]?.toLowerCase())
        )
      })

      return match || room
    },
    propertyFromRoom(room) {
      if (!room || room === 'Not Assigned') {
        return ''
      }

      const normalized = String(room).trim()

      // Strong signal: "<Property> - <Unit>" (e.g. "Emas Estate - B" -> "Emas Estate").
      if (normalized.includes(' - ')) {
        return normalized.split(' - ')[0].trim()
      }

      // Weak signal: "<Property> <Unit>" (e.g. "Aurum B" -> "Aurum").
      // Only trust it when it matches a known property, so we don't mangle
      // room numbers like "Room 101" into "Room".
      const parts = normalized.split(/\s+/)
      if (parts.length > 1) {
        const candidate = parts.slice(0, -1).join(' ')
        const knownProperties = this.getKnownPropertyNames()
        if (knownProperties.includes(candidate)) {
          return candidate
        }
      }

      return ''
    },
    getKnownPropertyNames() {
      // Collect property names from uplistingRooms.
      const uplistingNames = [
        ...new Set(
          this.uplistingRooms
            .map((nickname) => {
              const parts = nickname.split(' - ')
              return parts.length > 1 ? parts[0].trim() : null
            })
            .filter(Boolean)
        )
      ]

      // Also include API properties.
      const apiNames = (this.properties || [])
        .map((property) => property && property.name)
        .filter(Boolean)

      return [...new Set([...uplistingNames, ...apiNames])]
    },
    statusClass(status) {
      return status.toLowerCase().replace(/\s+/g, '-')
    },
    formatMessageContent(content) {
      if (!content) {
        return ''
      }

      const normalized = normalizeMessageText(content)
      const escaped = escapeHtml(normalized)

      return buildMessageHtml(escaped)
    },
    isAssistantMessage(message) {
      return message.role === 'assistant'
    },
    isSwitchMessage(message) {
      return Boolean(
        message && (message.role === 'system' || message.type === 'switch')
      )
    },
    showMessageActions(message) {
      if (this.isSwitchMessage(message)) {
        return false
      }

      if (this.canSwitchReplyRole && message.source === 'agent') {
        return false
      }

      return (
        this.isAssistantMessage(message) &&
        (!message.sources ||
          message.sources.length === 0 ||
          !message.sources.includes('uplisting'))
      )
    },
    messageBlockClass(message) {
      return this.isAssistantMessage(message) ? 'assistant' : 'guest'
    },
    isCompactMessage(index) {
      if (index === 0) {
        return false
      }

      const current = this.selectedMessages[index]
      const previous = this.selectedMessages[index - 1]
      if (this.isSwitchMessage(current) || this.isSwitchMessage(previous)) {
        return false
      }

      return (
        this.messageBlockClass(current) === this.messageBlockClass(previous)
      )
    }
  },
  mounted() {
    this.loadSavedAiModel()
    this.unsubscribeAuth = auth.onAuthStateChanged((user) => {
      this.currentUser = user
    })
    this.loadProperties()
    this.loadRooms().then(() => {
      this.loadConversations()
    })
  },
  destroyed() {
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth()
    }
    // Cleanup abort controllers
    if (this.pendingSessionRequest) {
      this.pendingSessionRequest.abort()
    }
    if (this.prefetchAbortController) {
      this.prefetchAbortController.abort()
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
    // Clear session cache
    this.sessionCache.clear()
    this.conversationListCache.clear()
  }
}
</script>

<style scoped>
.ai-conversation-screen-wrapper {
  position: relative;
  height: 100%;
  min-height: 0;
}

.sync-loader-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.72);
}

.sync-loader-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 420px);
  padding: 8px 24px 12px;
  text-align: center;
}

.sync-loader-gif {
  width: 220px;
  max-width: 72vw;
  height: auto;
}

.sync-loader-title {
  margin: 8px 0 10px;
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0.01em;
}

.sync-loader-subtitle {
  margin: 0;
  max-width: 340px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
}

.sync-loader-fade-enter-active,
.sync-loader-fade-leave-active {
  transition: opacity 0.2s ease;
}

.sync-loader-fade-enter-from,
.sync-loader-fade-leave-to {
  opacity: 0;
}

.ai-conversation-screen {
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 292px minmax(520px, 1fr) 380px;
  gap: 22px;
  padding: 20px 26px;
  overflow: hidden;
  background: #f0f1f3;
  color: #141414;
}

.ai-conversation-screen,
.ai-conversation-screen * {
  box-sizing: border-box !important;
}

.conversation-sidebar,
.chat-workspace,
.guest-panel {
  background: #ffffff;
}

.conversation-sidebar {
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #edf0f2;
}

.sidebar-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 31px 12px 21px;
}

.sidebar-heading h2 {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
}

.sync-button {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 35px;
  padding: 0 14px;
  border: 0;
  border-radius: 7px;
  background: #00530d;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.sync-button:disabled {
  cursor: default;
  opacity: 0.72;
}

.sync-icon {
  line-height: 1;
}

.conversation-search {
  height: 41px;
  margin: 0 12px 16px;
  padding: 0 18px;
  border: 1px solid #d1d9e1;
  border-radius: 4px;
  color: #3c3c3c;
  font-size: 14px;
  outline: none;
}

.conversation-search::placeholder {
  color: #949494;
  text-align: center;
}

.sync-error {
  margin: 0 12px 12px;
  padding: 9px 11px;
  border: 1px solid #f0b5ae;
  border-radius: 6px;
  background: #fef0ed;
  color: #9a2f23;
  font-size: 12px;
  line-height: 1.4;
}

.conversation-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid #cfd6dd;
}

.conversation-tabs button {
  height: 64px;
  border: 0;
  border-bottom: 4px solid transparent;
  background: #ffffff;
  color: #7c7c7c;
  font-size: 16px;
  cursor: pointer;
}

.conversation-tabs button.active {
  border-bottom-color: #00610f;
  color: #00610f;
  font-weight: 700;
}

.conversation-list {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
}

.conversation-list.is-paging {
  opacity: 0.55;
  pointer-events: none;
}

.conversation-row {
  width: 100%;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 12px 16px 10px;
  border: 0;
  border-bottom: 1px solid #cfd6dd;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
}

.conversation-row.selected {
  background: #f1faea;
  box-shadow: inset 0 0 0 1px #69ad45;
}

.conversation-topline,
.conversation-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.conversation-topline strong {
  color: #181818;
  font-size: 14px;
}

.conversation-topline span {
  color: #949494;
  font-size: 13px;
  white-space: nowrap;
}

.conversation-preview span:first-child {
  max-width: 210px;
  overflow: hidden;
  color: #171717;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unread-count {
  min-width: 22px;
  height: 24px;
  display: inline-grid;
  place-items: center;
  border-radius: 4px;
  background: #69ad45;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
}

.status-pill {
  width: fit-content;
  max-width: 100%;
  padding: 6px 12px;
  border-radius: 999px;
  color: #3b3b3b;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.status-pill.active-inquiry {
  background: #fcf2d3;
}

.status-pill.confirmed {
  background: #e5eee7;
}

.status-pill.needs-checkin,
.status-pill.pending-booking-request {
  background: #fefaed;
}

.status-pill.cancelled-booking-request {
  background: #fef0ed;
}

.conversation-pagination {
  display: grid;
  justify-items: center;
  gap: 7px;
  padding: 15px 12px 21px;
  color: #262626;
  font-size: 14px;
}

.pagination-pages {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  min-width: 0;
}

.pagination-numbers {
  display: flex;
  align-items: center;
  justify-content: safe center;
  gap: 2px;
  min-width: 0;
  overflow-x: auto;
}

.pagination-page {
  flex: 0 0 auto;
  min-width: 24px;
  padding: 2px 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  border: 0;
  background: transparent;
  color: #8f8383;
  font-weight: 500;
  cursor: pointer;
}

.pagination-page:hover:not(.pagination-page--selected) {
  color: #4f8532;
}

.pagination-page--selected {
  color: #4f8532;
  font-weight: 800;
  cursor: default;
}

.pagination-nav {
  flex: 0 0 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #8f8383;
  cursor: pointer;
}

.pagination-nav:hover:not(:disabled) {
  color: #4f8532;
}

.pagination-nav:disabled {
  opacity: 0.35;
  cursor: default;
}

.pagination-nav-icon {
  width: 14px;
  height: 14px;
  display: block;
}

.pagination-ellipsis {
  color: #8f8383;
  font-weight: 500;
  line-height: 1;
}

.chat-workspace {
  position: relative;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #edf0f2;
}

.chat-sending-progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9;
  height: 4px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.22);
}

.chat-sending-progress::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 38%;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.7);
  animation: sending-progress 1.15s ease-in-out infinite;
}

@keyframes sending-progress {
  0% {
    left: -38%;
  }
  100% {
    left: 100%;
  }
}

.chat-sending-overlay {
  position: absolute;
  top: 60px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(12, 28, 16, 0.42);
  backdrop-filter: blur(3px);
}

.sending-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 340px);
  padding: 28px 24px 24px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 18px 44px rgba(8, 24, 12, 0.28);
  text-align: center;
}

.sending-orb {
  position: relative;
  width: 58px;
  height: 58px;
  margin-bottom: 16px;
}

.sending-ring {
  position: absolute;
  inset: 0;
  border: 3px solid #e4eadc;
  border-top-color: #00530d;
  border-radius: 50%;
  animation: model-spin 0.85s linear infinite;
}

.sending-sparkle {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #55a624;
  font-size: 22px;
  line-height: 1;
  animation: model-sparkle 1.2s ease-in-out infinite;
}

.sending-title {
  margin: 0;
  color: #142014;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
}

.sending-subtitle {
  margin: 8px 0 0;
  max-width: 260px;
  color: #5d6b5e;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
}

.sending-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
}

.sending-dots i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #55a624;
  animation: sending-bounce 1s ease-in-out infinite;
}

.sending-dots i:nth-child(2) {
  animation-delay: 0.15s;
}

.sending-dots i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes sending-bounce {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-5px);
  }
}

.sending-fade-enter-active,
.sending-fade-leave-active {
  transition: opacity 0.2s ease;
}

.sending-fade-enter-active .sending-card,
.sending-fade-leave-active .sending-card {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.sending-fade-enter,
.sending-fade-enter-from,
.sending-fade-leave-to {
  opacity: 0;
}

.sending-fade-enter .sending-card,
.sending-fade-enter-from .sending-card,
.sending-fade-leave-to .sending-card {
  opacity: 0;
  transform: translateY(10px) scale(0.96);
}

.chat-title-bar {
  display: flex;
  align-items: center;
  min-height: 60px;
  padding: 0 17px;
  background: #00530d;
}

.chat-title-bar h1 {
  margin: 0;
  color: #ffffff;
  font-size: 22px;
  font-weight: 700;
}

.guest-alert {
  height: 36px;
  display: flex;
  align-items: center;
  padding: 0 17px;
  background: #fff9df;
  color: #b37900;
  font-size: 14px;
  font-weight: 700;
}

.room-assignment-row {
  height: 72px;
  display: flex;
  align-items: center;
  gap: 34px;
  padding: 0 17px;
  border-bottom: 1px solid #d5dce4;
  background: #ffffff;
}

.room-assignment-row label,
.room-assignment {
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
  flex: 1;
  color: #697385;
  font-size: 16px;
}

.room-assignment-row select,
.room-assignment-row input {
  width: 100%;
  min-width: 0;
  height: 38px;
  padding: 0 16px !important;
  border: 1px solid #d7dce3;
  border-radius: 9px;
  background: #ffffff;
  color: #4b5566;
  font-size: 16px;
  font-weight: 700;
  opacity: 1;
}

.room-assignment-row select:disabled,
.room-assignment-row input:disabled {
  background: #eef0f3;
  border-color: #dfe3e8;
  color: #8a94a3;
  cursor: not-allowed;
}

.room-assignment strong {
  color: #4b5566;
  font-size: 16px;
}

.message-stream {
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px 24px;
  overflow-y: auto;
  background: #fafbfc;
}

.empty-message-state {
  color: #687283;
  font-size: 15px;
}

.messages-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 16px;
  color: #687283;
  font-size: 14px;
}

.messages-loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e4eadc;
  border-top-color: #55a624;
  border-radius: 50%;
  animation: model-spin 0.7s linear infinite;
}

.chat-error {
  width: fit-content;
  max-width: min(72%, 520px);
  margin-top: 4px;
  padding: 10px 14px;
  border: 1px solid #f0b5ae;
  border-radius: 10px;
  background: #fef0ed;
  color: #9a2f23;
  font-size: 13px;
}

.message-block {
  display: flex;
  width: 100%;
}

.message-block--compact {
  margin-top: -8px;
}

.message-block.guest {
  justify-content: flex-start;
}

.message-block.assistant {
  justify-content: flex-end;
}

.message-content {
  display: flex;
  flex-direction: column;
  max-width: min(72%, 520px);
  min-width: 72px;
}

.message-block.guest .message-content {
  align-items: flex-start;
}

.message-block.assistant .message-content {
  align-items: flex-end;
}

.message-bubble {
  width: fit-content;
  max-width: 100%;
  padding: 11px 16px;
  border-radius: 14px;
  color: #4b5566;
  font-size: 15px;
  line-height: 1.55;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: start;
}

.message-bubble ::v-deep > *:first-child {
  margin-top: 0;
}

.message-bubble ::v-deep > *:last-child {
  margin-bottom: 0;
}

.message-bubble ::v-deep p {
  margin: 10px 0 0;
}

.message-bubble ::v-deep ul,
.message-bubble ::v-deep ol {
  margin: 10px 0 0;
  padding-left: 22px;
}

.message-bubble ::v-deep li {
  margin-bottom: 4px;
}

.message-bubble ::v-deep li:last-child {
  margin-bottom: 0;
}

.message-bubble ::v-deep li::marker {
  color: #8b95a3;
}

.message-bubble ::v-deep strong {
  font-weight: 700;
}

.message-bubble ::v-deep em {
  font-style: italic;
}

.message-bubble ::v-deep code {
  padding: 2px 5px;
  border-radius: 4px;
  background: rgba(20, 20, 20, 0.06);
  font-size: 0.92em;
}

.message-bubble ::v-deep a {
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
  word-break: break-all;
}

.message-block.guest .message-bubble {
  border: 1px solid #e4e8ee;
  border-radius: 14px 14px 14px 4px;
  background: #f2f4f7;
  color: #2f3747;
}

.message-block.assistant .message-bubble {
  border: 1px solid #dfe4ea;
  border-radius: 14px 14px 4px 14px;
  background: #ffffff;
  color: #4b5566;
  box-shadow: 0 1px 2px rgba(20, 20, 20, 0.04);
}

.message-footer {
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 12px;
  width: 100%;
  margin-top: 6px;
}

.ai-generated-label {
  margin-left: auto;
  color: #8b95a3;
  font-size: 13px;
  font-style: italic;
  line-height: 1;
  white-space: nowrap;
}

.message-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  margin-left: 4px;
}

.message-actions button {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #7a8796;
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
}

.message-actions button:hover {
  background: #eef1f5;
  color: #4b5566;
}

.message-actions button:disabled {
  cursor: default;
  opacity: 0.45;
}

.message-actions button:disabled:hover {
  background: transparent;
  color: #7a8796;
}

.message-actions .regenerate-button {
  font-size: 21px;
  font-weight: 600;
}

.chat-composer {
  display: grid;
  gap: 8px;
  padding: 17px 19px;
  border-top: 1px solid #edf0f2;
  background: #ffffff;
}

.sandbox-role-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: -17px -19px 10px;
  padding: 8px 16px;
  border-bottom: 1px solid #eceff3;
  background: #f4f5f7;
}

.sandbox-role-bar--guest {
  background: #f0f9eb;
  border-bottom-color: #dcefd0;
}

.sandbox-role-copy {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  min-width: 0;
  color: #1a1a1a;
  overflow: hidden;
}

.sandbox-role-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: inline-grid;
  place-items: center;
  color: #2f2f2f;
}

.sandbox-role-icon--guest {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #3a9a2a;
  color: #ffffff;
}

.sandbox-role-copy strong {
  color: #1a1a1a;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  white-space: nowrap;
}

.sandbox-role-hint {
  color: #8a93a0;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sandbox-role-switch {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 10px;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #d0d5dc;
  border-radius: 999px;
  background: #ffffff;
  color: #3a4250;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}

.sandbox-role-switch:hover:not(:disabled) {
  border-color: #b8c0ca;
  background: #f7f8fa;
}

.sandbox-role-switch:disabled {
  cursor: default;
  opacity: 0.55;
}

.message-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 4px 0 8px;
  color: #9aa4b0;
  font-size: 11px;
  font-style: italic;
  line-height: 1.4;
}

.message-switch::before,
.message-switch::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e4e8ee;
}

.message-switch span {
  flex-shrink: 0;
  white-space: nowrap;
}

.chat-composer textarea {
  width: 100%;
  min-height: 96px;
  padding: 12px 14px;
  border: 1px solid #dfe4e9;
  border-radius: 8px;
  resize: vertical;
  color: #343434;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.45;
  outline: none;
}

.chat-composer textarea::placeholder {
  color: #b3b3b3;
}

.manual-composer-actions,
.ai-draft-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.manual-composer-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.generate-ai-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  border: 1px solid #dfe4e9;
  border-radius: 999px;
  background: #ffffff;
  color: #343434;
  font-size: 14px;
  cursor: pointer;
}

.generate-ai-button span,
.ai-sparkle {
  margin-right: 6px;
  color: #55a624;
}

.ai-draft-editor {
  position: relative;
}

.ai-draft-editor .ai-sparkle {
  position: absolute;
  top: 13px;
  left: 14px;
  z-index: 1;
}

.ai-draft-editor textarea {
  min-height: 108px;
  padding-left: 38px;
  color: #536074;
  font-style: italic;
}

.change-model-button {
  padding: 0;
  border: 0;
  border-bottom: 1px solid currentColor;
  background: transparent;
  color: #386013;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.model-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 24px 24px;
  background: rgba(20, 20, 20, 0.45);
}

.model-modal {
  width: min(100%, 420px);
  padding: 22px 22px 20px;
  border: 1px solid #eef0f2;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 16px 40px rgba(20, 20, 20, 0.14);
}

.model-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 25px;
}

.model-modal-header h3 {
  margin: 0;
  color: #141414;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.model-modal-close {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border: 0.5px solid #141414;
  border-radius: 8px;
  background: #ffffff;
  color: #141414;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.model-modal-close:hover {
  background: #f4f4f4;
}

.model-modal-body {
  display: grid;
  gap: 25px;
  margin-bottom: 25px;
}

.model-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 168px;
  padding: 8px 8px 4px;
  text-align: center;
}

.model-loading-orb {
  position: relative;
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
}

.model-loading-ring {
  position: absolute;
  inset: 0;
  border: 2.5px solid #e4eadc;
  border-top-color: #00530d;
  border-radius: 50%;
  animation: model-spin 0.85s linear infinite;
}

.model-loading-sparkle {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #55a624;
  font-size: 20px;
  line-height: 1;
  animation: model-sparkle 1.2s ease-in-out infinite;
}

.model-loading-title {
  margin: 0 0 6px;
  color: #141414;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}

.model-loading-subtitle {
  margin: 0;
  color: #687283;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
}

@keyframes model-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes model-sparkle {
  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

/* ---- error state ---- */
.model-error-state {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #f5c6c0;
  border-radius: 10px;
  background: #fff5f4;
}

.model-error-icon {
  flex-shrink: 0;
  color: #c0392b;
  font-size: 18px;
  line-height: 1.2;
}

.model-error-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.model-error-message {
  margin: 0;
  color: #7d1f17;
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;
}

.model-error-retry {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  color: #9a2f23;
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.model-error-retry:hover {
  color: #6b1e15;
}

.model-step {
  display: grid;
  gap: 10px;
}

.model-step-label {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #141414;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

.model-step-number {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: #00530d;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.model-modal-body ::v-deep .model-select.multiselect {
  min-height: unset;
}

.model-modal-body ::v-deep .model-select.multiselect--disabled {
  opacity: 0.55;
  pointer-events: none;
}

.model-modal-body ::v-deep .model-select .multiselect__tags {
  display: flex;
  align-items: center;
  min-height: 44px;
  height: 44px;
  padding: 0 38px 0 14px;
  border: 1px solid #dde1e6;
  border-radius: 10px;
  background: #ffffff;
}

.model-modal-body
  ::v-deep
  .model-select.multiselect--active
  .multiselect__tags {
  border-color: #141414;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.model-modal-body ::v-deep .model-select .multiselect__tags-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 0;
}

.model-modal-body ::v-deep .model-select .multiselect__single {
  display: block;
  margin: 0;
  padding: 0;
  color: #141414;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.model-modal-body ::v-deep .model-select .multiselect__placeholder {
  margin: 0;
  padding: 0;
  color: #949494;
  font-size: 14px;
  line-height: 1.2;
}

.model-modal-body ::v-deep .model-select .multiselect__select {
  top: 50%;
  right: 4px;
  width: 30px;
  height: 30px;
  padding: 0;
  transform: translateY(-50%);
}

.model-modal-body ::v-deep .model-select .multiselect__select::before {
  top: 50%;
  margin-top: -2px;
  border-width: 5px 5px 0;
  border-color: #141414 transparent transparent;
}

.model-modal-body ::v-deep .model-select .multiselect__content-wrapper {
  border: 1px solid #141414;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 10px 10px;
  box-shadow: 0 10px 24px rgba(20, 20, 20, 0.08);
}

.model-modal-body ::v-deep .model-select .multiselect__option {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 0 14px;
  border-bottom: 1px solid #f0f1f3;
  color: #2a2a2a;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.model-modal-body ::v-deep .model-select .multiselect__option:last-child {
  border-bottom: 0;
}

.model-modal-body ::v-deep .model-select .multiselect__option--highlight {
  background: #f1f2f4;
  color: #141414;
}

.model-modal-body ::v-deep .model-select .multiselect__option--selected {
  background: #ffffff;
  color: #141414;
  font-weight: 700;
}

.model-modal-body
  ::v-deep
  .model-select
  .multiselect__option--selected.multiselect__option--highlight {
  background: #f1f2f4;
}

.model-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 2px;
}

.model-modal-cancel,
.model-modal-confirm {
  min-width: 120px;
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.model-modal-cancel {
  border: 1.5px solid #141414;
  background: #ffffff;
  color: #141414;
}

.model-modal-cancel:hover {
  background: #f4f4f4;
}

.model-modal-confirm {
  border: 0;
  background: #00530d;
  color: #ffffff;
}

.model-modal-confirm:hover {
  background: #00400a;
}

.model-modal-confirm:disabled {
  cursor: default;
  opacity: 0.55;
}

.send-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 130px;
  height: 36px;
  border: 0;
  border-radius: 999px;
  background: #00530d;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.send-button-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: model-spin 0.7s linear infinite;
}

.generate-button-spinner {
  margin-right: 6px;
  border-color: rgba(52, 52, 52, 0.18);
  border-top-color: #55a624;
}

.approve-send-button {
  width: 170px;
}

.generate-ai-button:disabled,
.send-button:disabled {
  cursor: default;
  opacity: 0.55;
}

.guest-panel {
  height: 100%;
  overflow-y: auto;
  padding: 22px 21px 20px;
  border: 1px solid #edf0f2;
}

.guest-panel h2 {
  margin: 0 0 22px;
  color: #030303;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
}

.guest-contact {
  display: grid;
  gap: 12px;
  margin-bottom: 20px;
}

.guest-contact label {
  display: grid;
  grid-template-columns: 70px 1fr;
  align-items: center;
  gap: 10px;
  color: #252525;
  font-size: 14px;
}

.guest-contact input {
  width: 100%;
  min-width: 0;
  height: 34px;
  padding: 0 12px;
  border: 1px solid #e0e3e8;
  border-radius: 8px;
  background: #ffffff;
  color: #2a2a2a;
  font-size: 14px;
}

.guest-contact input:disabled {
  background: #f4f5f7;
  color: #9a9a9a;
  cursor: not-allowed;
}

.guest-divider {
  height: 2px;
  margin-bottom: 10px;
  background: #d2dae2;
}

.guest-field {
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: center;
  gap: 12px;
  min-height: 50px;
}

.guest-field span {
  color: #262626;
  font-size: 14px;
  text-align: left;
}

.guest-field input {
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 17px;
  border: 1px solid #e0e3e8;
  border-radius: 8px;
  background: #ffffff;
  color: #2a2a2a;
  font-size: 14px;
  opacity: 1;
  text-align: right;
}

.guest-field input:disabled {
  background: #f4f5f7;
  color: #9a9a9a;
  cursor: not-allowed;
}

.guest-field.first-message strong {
  color: #8f8f8f;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
}

@media (max-width: 1360px) {
  .ai-conversation-screen {
    grid-template-columns: 260px minmax(0, 1fr) 320px;
    gap: 16px;
    padding-right: 16px;
    padding-left: 16px;
  }

  .guest-field {
    grid-template-columns: 120px minmax(0, 1fr);
  }

  .room-assignment-row {
    gap: 16px;
  }
}

@media (max-width: 980px) {
  .ai-conversation-screen {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
    padding: 16px;
  }

  .conversation-sidebar,
  .chat-workspace {
    min-height: auto;
  }

  .guest-panel {
    grid-column: auto;
    width: 100%;
  }
}
</style>
